import { normalizeEventDefinition } from "./eventCompetition";
import { legacyEventDefinitionFromDisplayName } from "./eventCompetitionLegacy";

/**
 * Tournament Event aggregate (ADR-031 P3).
 * Competitive execution boundary under Tournament Module.
 * Storage: arenas/{arenaId}/events/{eventId}/(entries|draws|fixtures)
 *
 * Display (`display.name`) is human-facing only.
 * Competition rules (`competition`) drive entries, draws, and sport resolution.
 * See docs/ARCHITECTURE/TOURNAMENT_EVENT.md
 */

/**
 * @typedef {'draft'|'published'|'registration_open'|'registration_closed'|'in_progress'|'completed'|'archived'|'inactive'} EventStatus
 */

/**
 * @typedef {Object} ManagedEventSummary
 * @property {string} eventId
 * @property {string} name
 * @property {{ name: string }} [display]
 * @property {import('./eventCompetition').EventCompetition} [competition]
 * @property {EventStatus} [status]
 * @property {number} [createdAtMs]
 */

/**
 * @typedef {Object} TournamentEvent
 * @property {string} eventId
 * @property {string} arenaId
 * @property {string} name
 * @property {{ name: string }} display
 * @property {import('./eventCompetition').EventCompetition} competition
 * @property {string | null} [organizationId]
 * @property {string} createdBy
 * @property {EventStatus} status
 * @property {object} [settings]
 * @property {object | null} [standings]
 * @property {number} [createdAtMs]
 * @property {number} [updatedAtMs]
 */

export const EVENT_STATUSES = Object.freeze({
  DRAFT: "draft",
  PUBLISHED: "published",
  REGISTRATION_OPEN: "registration_open",
  REGISTRATION_CLOSED: "registration_closed",
  IN_PROGRESS: "in_progress",
  COMPLETED: "completed",
  ARCHIVED: "archived",
  /** Soft-removed from active tournament UI; entries/draws retained. */
  INACTIVE: "inactive",
});

/** Active = usable for registration / draws (not inactive). */
export function isEventActive(eventOrSummary) {
  const st = String(eventOrSummary?.status || EVENT_STATUSES.DRAFT).toLowerCase();
  return st !== EVENT_STATUSES.INACTIVE;
}

export function isEventInactive(eventOrSummary) {
  return !isEventActive(eventOrSummary);
}

export function newEventId() {
  if (typeof crypto !== "undefined" && typeof crypto.randomUUID === "function") {
    return `evt_${crypto.randomUUID()}`;
  }
  return `evt_${Date.now()}_${Math.random().toString(36).slice(2, 11)}`;
}

/**
 * @param {object} input
 * @returns {ManagedEventSummary}
 */
export function createManagedEventSummary(input = {}) {
  const fromStructured = normalizeEventDefinitionInput(input);
  if (fromStructured) {
    return {
      eventId: input.eventId || newEventId(),
      name: fromStructured.display.name,
      display: fromStructured.display,
      competition: fromStructured.competition,
      status: input.status || EVENT_STATUSES.DRAFT,
      createdAtMs: input.createdAtMs ?? Date.now(),
    };
  }
  const legacyName = String(input.name || "").trim() || "Main event";
  const legacyDef = legacyEventDefinitionFromDisplayName(legacyName);
  return {
    eventId: input.eventId || newEventId(),
    name: legacyDef.display.name,
    display: legacyDef.display,
    competition: legacyDef.competition,
    status: input.status || EVENT_STATUSES.DRAFT,
    createdAtMs: input.createdAtMs ?? Date.now(),
  };
}

/**
 * @param {object} input
 */
function normalizeEventDefinitionInput(input) {
  if (input.display && input.competition) {
    return normalizeEventDefinition(input);
  }
  if (input.competition && input.name) {
    return normalizeEventDefinition(input);
  }
  return null;
}

/**
 * Full Event document shell for arenas/{id}/events/{eventId}.
 * @param {object} input
 */
export function createTournamentEventShell(input = {}) {
  const summary = createManagedEventSummary(input);
  const now = input.nowMs ?? Date.now();
  return {
    eventId: summary.eventId,
    arenaId: input.arenaId || null,
    name: summary.name,
    display: summary.display,
    competition: summary.competition,
    status: input.status || summary.status,
    organizationId: input.organizationId ?? null,
    createdBy: input.createdBy || null,
    settings: input.settings || {},
    standings: input.standings ?? null,
    createdAtMs: input.createdAtMs ?? summary.createdAtMs ?? now,
    updatedAtMs: input.updatedAtMs ?? now,
  };
}

export {
  COMPETITION_EVENT_PRESETS,
  CUSTOM_EVENT_OPTION,
  resolveCompetitionEventName,
} from "./eventPresets";
